import { useState, useEffect, useCallback } from 'react'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { showNotification } from '@/utils/notifications'
import { isValidUrl } from '@/lib/utils.js'

export const useAdContent = (initialData = {}) => {
    const { setStepContentData } = useWizard()

    // Initialize form state with default values
    const [formData, setFormData] = useState({
        primaryText: initialData.primaryText || [''],
        headline: initialData.headline || [''],
        description: initialData.description || [''],
        cta: initialData.cta || 'LEARN_MORE',
        destinationUrl: initialData.destinationUrl || '',
        displayLink: initialData.displayLink || '',
    })

    const [errors, setErrors] = useState({})

    // Update wizard context whenever form data changes (explicit step name to avoid saving to wrong step on navigation)
    useEffect(() => {
        setStepContentData(formData, 'setAdContent')
    }, [formData, setStepContentData])

    // Update individual field
    const updateField = useCallback((fieldName, value) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }))
        setErrors((prev) => (prev[fieldName] ? { ...prev, [fieldName]: undefined } : prev))
    }, [])

    // Update multiple fields at once
    const updateFields = useCallback((updates) => {
        setFormData((prev) => ({
            ...prev,
            ...updates,
        }))
    }, [])

    // Validate form data
    const validate = useCallback(
        (showInstantForm) => {
            // Validation - only destination URL is required when lead form is not selected
            if (!showInstantForm && !formData.destinationUrl.trim()) {
                setErrors({ destinationUrl: 'Destination URL is required' })
                showNotification('Destination URL is required', 'error', 3000, null, null, true, true)
                return false
            } else if (formData.destinationUrl && !isValidUrl(formData.destinationUrl)) {
                setErrors({ destinationUrl: 'Invalid URL format' })
                showNotification('Invalid URL format', 'error', 3000, null, null, true, true)
                return false
            }
            setErrors({})
            return true
        },
        [formData.destinationUrl],
    )

    // Reset form to initial state
    const reset = useCallback(() => {
        setFormData({
            primaryText: [''],
            headline: [''],
            description: [''],
            cta: 'LEARN_MORE',
            destinationUrl: '',
            displayLink: '',
        })
    }, [])

    return {
        formData,
        updateField,
        updateFields,
        validate,
        errors,
        reset,
        // Individual setters for backward compatibility
        setPrimaryText: (value) => updateField('primaryText', value),
        setHeadline: (value) => updateField('headline', value),
        setDescription: (value) => updateField('description', value),
        setCta: (value) => updateField('cta', value),
        setDestinationUrl: (value) => updateField('destinationUrl', value),
        setDisplayLink: (value) => updateField('displayLink', value),
    }
}
